import React from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {Flex, noop, PriorityIcon} from '@priority-software/priority-style-react';
import {closeFormBySession} from '../../../actions/form';
import {Breadcrumbs} from '../Breadcrumbs';
import {formsStore} from '../../../../../stores/Form/FormsStore';
import {BackButton} from '../BackButton';
import {UpdateStatus} from '../UpdateStatus';
import {undoAction} from '../../../actions/undo';
import {HeaderHelpButton} from '../../../../../components/commonOldtoNew';
import {MailboxSendToggle} from '../../../../../components/MailboxSendToggle';
import {createDataTestId} from '../../../../../utils/dataTestId';
import aiERPlabel from '../../../../../images/aiERPlabel.svg';
import {addDeleteFavorites} from '../../../../../_shared/GWTBridge/invoke/priApi';
import {texts} from '../../AdvancedSearch/AdvancedSearchDrawer/texts';
import {IconWithTooltip} from '../../../../../components/Common/IconWithTooltip';
import {
    handleMailboxToggleChange,
    handleUnsupportedFeature
} from '../../../actions/handleUnsupportedFeature';
import style from './styles.module.scss';

type Props = {
    session: string;
    breadcrumbs: any;
    onBreadCrumbClick: (level: number, session: string) => void;
    componentClasses?: string;
    showMailboxTogglePopup?: boolean;
    setShowMailboxTogglePopup?: (value: boolean) => void;
};

export const ScreenHeader = observer(
    ({
        session,
        breadcrumbs,
        onBreadCrumbClick,
        componentClasses,
        showMailboxTogglePopup,
        setShowMailboxTogglePopup
    }: Props) => {
        const closeScreen = async () => {
            const activeForm = formsStore.getActiveForm();
            if (activeForm?.parentForm) {
                const isValidAction = await formsStore.currentScreen.handleFormLevel(
                    activeForm.parentForm?.level
                );
                if (isValidAction) {
                    closeScreen();
                }
            } else {
                closeFormBySession(session, !screen?.zoomSource);
            }
        };

        const handleFavoriteClick = () => {
            addDeleteFavorites(
                Number(formsStore.currentScreen.form.form.Form),
                formsStore.currentScreen.form.title,
                !formsStore.currentScreen.form.isFavorite
            );
            formsStore.currentScreen.form.setIsFavorite(!formsStore.currentScreen.form.isFavorite);
        };

        const screen = formsStore.getScreenBySession(session);
        const shouldShowAILabel = screen?.breadcrumbs?.[0]?.ename === 'PREDICTIONMODEL';
        const activeForm = formsStore.getActiveForm();

        return (
            <Flex
                componentClasses={cx(style.header, componentClasses)}
                alignItems="center"
                justifyContent="space-between"
                {...createDataTestId('screen-title-container', true)}
            >
                <Flex justifyContent="flex-start">
                    <Breadcrumbs data={breadcrumbs} onItemClick={onBreadCrumbClick} />
                    {screen?.zoomSource &&
                        formsStore.getActiveForm()?.level === 0 &&
                        formsStore.getActiveForm()?.searchModeLevel === 0 && (
                            <BackButton screen={screen} />
                        )}
                    {shouldShowAILabel && (
                        <img src={aiERPlabel} alt="ai-erp-label" className={style.aiERPlabel} />
                    )}
                    <IconWithTooltip
                        tooltip={texts.addToFavoritesTooltip}
                        componentClasses={style.favoriteBtn}
                        icon={
                            formsStore.currentScreen.form.isFavorite
                                ? PriorityIcon.starFilled
                                : PriorityIcon.starEmpty
                        }
                        onClick={handleFavoriteClick}
                    />
                </Flex>

                <Flex justifyContent="flex-end" componentClasses={style.gapSm}>
                    {!activeForm?.form?.Ishtml ? (
                        <UpdateStatus session={session} supportHtmlForm={false} />
                    ) : null}

                    <MailboxSendToggle
                        currentForm={activeForm}
                        componentClasses={style.itemMargin}
                        onToggleChange={handleMailboxToggleChange}
                        showMailboxTogglePopup={showMailboxTogglePopup}
                        setShowMailboxTogglePopup={setShowMailboxTogglePopup}
                    />

                    <IconWithTooltip
                        tooltip={texts.undoChangeTooltip}
                        componentClasses={style.undoBtn}
                        onClick={undoAction}
                        icon={PriorityIcon.refresh}
                    />

                    <IconWithTooltip
                        onClick={() => handleUnsupportedFeature('ExportToExcel')}
                        iconColor="primary-700"
                        icon={PriorityIcon.exportExcel}
                        tooltip={texts.exportToExcelTooltip}
                    />

                    <IconWithTooltip
                        iconColor="primary-700"
                        onClick={noop}
                        icon={PriorityIcon.settings}
                        tooltip={texts.formSettingsTooltip}
                    />

                    <div className={style.divider} />

                    <HeaderHelpButton tooltipPosition="bottom-end" />

                    <IconWithTooltip
                        tooltip={texts.formCloseTooltip}
                        icon={PriorityIcon.closeBig}
                        onClick={closeScreen}
                        componentClasses={style.closeIcon}
                    />
                </Flex>
            </Flex>
        );
    }
);
